import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaUndo, 
  FaTrash, 
  FaExclamationTriangle, 
  FaClock, 
  FaUser, 
  FaCalendarAlt,
  FaCheckSquare,
  FaSquare,
  FaSync
} from 'react-icons/fa';
import { 
  fetchDeletedProducts, 
  restoreDeletedProduct, 
  permanentlyDeleteProducts 
} from '../services/apiService';
import LoadingSpinner from './LoadingSpinner';
import Pagination from './Pagination';
import { useLanguage } from '../contexts/LanguageContext';
import KpiCard from './dashboard/KpiCard';

const DeletedProductsManager = () => {
  const { t } = useLanguage();
  const [deletedProducts, setDeletedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isPermanentlyDeleting, setIsPermanentlyDeleting] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showPermanentDeleteModal, setShowPermanentDeleteModal] = useState(false);

  const fetchDeletedProductsData = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchDeletedProducts(page, 20);
      
      if (response.success) {
        setDeletedProducts(response.data.products);
        setTotalPages(response.data.pages);
        setTotalProducts(response.data.total);
        setCurrentPage(page);
      } else {
        setError(response.message || t('deletedProducts.errors.fetchDeletedProducts'));
      }
    } catch (error) {
      console.error('Error fetching deleted products:', error);
      setError(error.response?.data?.message || t('deletedProducts.errors.fetchDeletedProducts'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeletedProductsData(1);
  }, []);

  const handlePageChange = (page) => {
    fetchDeletedProductsData(page);
  };

  const handleSelectProduct = (product) => {
    setSelectedProducts(prevSelected => {
      const isAlreadySelected = prevSelected.some(p => p._id === product._id);
      if (isAlreadySelected) {
        return prevSelected.filter(p => p._id !== product._id);
      } else {
        return [...prevSelected, product];
      }
    });
  };

  // Helper to check if all products on the current page are selected
  const areAllCurrentPageSelected = deletedProducts.length > 0 && deletedProducts.every(product => selectedProducts.some(p => p._id === product._id));
  // Helper to check if some (but not all) products on the current page are selected
  const isSomeCurrentPageSelected = deletedProducts.some(product => selectedProducts.some(p => p._id === product._id)) && !areAllCurrentPageSelected;

  const handleSelectAllCurrentPage = () => {
    if (areAllCurrentPageSelected) {
      // Deselect all products on this page
      setSelectedProducts(prevSelected => prevSelected.filter(p => !deletedProducts.some(dp => dp._id === p._id)));
    } else {
      // Add all products on this page that aren't already selected
      setSelectedProducts(prevSelected => {
        const newSelections = deletedProducts.filter(product => !prevSelected.some(p => p._id === product._id));
        return [...prevSelected, ...newSelections];
      });
    }
  };

  const handleRestore = async () => {
    if (selectedProducts.length === 0) {
      setError(t('deletedProducts.errors.selectOne'));
      return;
    }

    try {
      setIsRestoring(true);
      setError(null);

      const pharmacodes = selectedProducts.flatMap(product => product.pharmacodes);
      const response = await restoreDeletedProduct(pharmacodes);

      if (response.success) {
        // Remove restored products from the list
        setDeletedProducts(prevProducts => 
          prevProducts.filter(product => 
            !selectedProducts.some(selected => selected._id === product._id)
          )
        );
        
        setSelectedProducts([]);
        setShowRestoreModal(false);
        
        // Refresh the list
        fetchDeletedProductsData(currentPage);
      } else {
        setError(response.message || t('deletedProducts.errors.restoreProducts'));
      }
    } catch (error) {
      console.error('Error restoring products:', error);
      setError(error.response?.data?.message || t('deletedProducts.errors.restoreProducts'));
    } finally {
      setIsRestoring(false);
    }
  };

  const handlePermanentDelete = async () => {
    try {
      setIsPermanentlyDeleting(true);
      setError(null);

      const response = await permanentlyDeleteProducts();

      if (response.success) {
        setShowPermanentDeleteModal(false);
        
        // Refresh the list
        fetchDeletedProductsData(currentPage);
      } else {
        setError(response.message || t('deletedProducts.errors.permanentDeleteProducts'));
      }
    } catch (error) {
      console.error('Error permanently deleting products:', error);
      setError(error.response?.data?.message || t('deletedProducts.errors.permanentDeleteProducts'));
    } finally {
      setIsPermanentlyDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysUntilPermanentDelete = (deletedAt) => {
    const deletedDate = new Date(deletedAt);
    const fiftyDaysLater = new Date(deletedDate.getTime() + (50 * 24 * 60 * 60 * 1000));
    const now = new Date();
    const diffTime = fiftyDaysLater - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const DeletedProductCard = ({ product, onSelect, isSelected = false }) => {
    const { t } = useLanguage();
    const daysUntilPermanent = getDaysUntilPermanentDelete(product.deletedAt);
    const canRestore = product.canRestore;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={`bg-white rounded-xl shadow-sm border transition-all duration-200 hover:shadow-md ${
          isSelected ? 'border-indigo-500 ring-1 ring-indigo-200 bg-indigo-50/40' : 'border-gray-200'
        }`}
      >
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              {isSelectionMode && (
                <button
                  onClick={() => onSelect(product)}
                  className="text-blue-600 hover:text-blue-800 transition-colors"
                >
                  {isSelected ? <FaCheckSquare size={20} /> : <FaSquare size={20} />}
                </button>
              )}
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {product.longDescription}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('deletedProducts.productCard.pharmacodes')}: {product.pharmacodes.join(', ')}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {canRestore ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <FaUndo className="mr-1" />
                  {t('deletedProducts.productCard.canRestore')}
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <FaExclamationTriangle className="mr-1" />
                  {t('deletedProducts.productCard.expired')}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <FaCalendarAlt className="text-gray-400" />
              <span>{t('deletedProducts.productCard.deleted')}: {formatDate(product.deletedAt)}</span>
            </div>
            
            {product.deletedBy && (
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <FaUser className="text-gray-400" />
                <span>{t('deletedProducts.productCard.by')}: {product.deletedBy.name || product.deletedBy.email}</span>
              </div>
            )}
          </div>

          {canRestore ? (
            <div className="flex items-center space-x-2 text-sm text-blue-600">
              <FaClock className="text-blue-400" />
              <span>
                {t('deletedProducts.productCard.daysUntilPermanent', { days: daysUntilPermanent })}
              </span>
            </div>
          ) : (
            <div className="flex items-center space-x-2 text-sm text-red-600">
              <FaExclamationTriangle className="text-red-400" />
              <span>{t('deletedProducts.productCard.permanentlyDeletedAfter')}</span>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="mx-auto max-w-[95%] px-2 sm:px-4 lg:px-6 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-gray-900">{t('deletedProducts.title')}</h1>
              <p className="text-sm text-gray-500 mt-1">
                {t('deletedProducts.subtitle')}
              </p>
            </div>
            <div className="flex space-x-3 items-center">
              {isSelectionMode && (
                <button
                  onClick={handleSelectAllCurrentPage}
                  className={`flex items-center px-4 py-2 rounded-md text-sm font-medium border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    areAllCurrentPageSelected || isSomeCurrentPageSelected
                      ? 'bg-blue-100 border-blue-400 text-blue-700 hover:bg-blue-200'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                  aria-pressed={areAllCurrentPageSelected}
                >
                  <input
                    type="checkbox"
                    className="form-checkbox h-5 w-5 text-blue-600 mr-2"
                    checked={areAllCurrentPageSelected}
                    ref={el => {
                      if (el) el.indeterminate = isSomeCurrentPageSelected;
                    }}
                    readOnly
                  />
                  {areAllCurrentPageSelected
                    ? t('deletedProducts.selection.deselectAllOnPage')
                    : t('deletedProducts.selection.selectAllOnPage')}
                </button>
              )}
              <button
                onClick={() => setIsSelectionMode(!isSelectionMode)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  isSelectionMode
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {isSelectionMode ? t('deletedProducts.selection.cancelSelection') : t('deletedProducts.selection.selectProducts')}
              </button>
              
              {isSelectionMode && selectedProducts.length > 0 && (
                <button
                  onClick={() => setShowRestoreModal(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors"
                >
                  {t('deletedProducts.selection.restoreSelected', { count: selectedProducts.length })}
                </button>
              )}
              
              <button
                onClick={() => setShowPermanentDeleteModal(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors"
              >
                {t('deletedProducts.selection.cleanupExpired')}
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <KpiCard
            icon={FaTrash}
            label={t('deletedProducts.stats.totalDeleted')}
            value={totalProducts.toLocaleString()}
            accent="gray"
            delay={0}
          />
          <KpiCard
            icon={FaUndo}
            label={t('deletedProducts.stats.canRestore')}
            value={deletedProducts.filter(p => p.canRestore).length.toLocaleString()}
            accent="green"
            delay={0.05}
          />
          <KpiCard
            icon={FaExclamationTriangle}
            label={t('deletedProducts.stats.expired')}
            value={deletedProducts.filter(p => !p.canRestore).length.toLocaleString()}
            accent="red"
            delay={0.1}
          />
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <FaExclamationTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{t('deletedProducts.error')}</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Products Grid */}
        {deletedProducts.length === 0 ? (
          <div className="text-center py-12">
            <FaTrash className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('deletedProducts.empty.title')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {t('deletedProducts.empty.description')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <AnimatePresence>
              {deletedProducts.map((product) => (
                <DeletedProductCard
                  key={product._id}
                  product={product}
                  onSelect={handleSelectProduct}
                  isSelected={selectedProducts.some(p => p._id === product._id)}
                />
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Bottom Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>

      {/* Restore Confirmation Modal */}
      <AnimatePresence>
        {showRestoreModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            >
              <div className="flex items-center mb-4">
                <FaUndo className="h-6 w-6 text-green-600 mr-3" />
                <h3 className="text-lg font-medium text-gray-900">{t('deletedProducts.modals.restore.title')}</h3>
              </div>
              <p className="text-gray-600 mb-6">
                {t('deletedProducts.modals.restore.description', { count: selectedProducts.length })}
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowRestoreModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t('deletedProducts.modals.restore.cancel')}
                </button>
                <button
                  onClick={handleRestore}
                  disabled={isRestoring}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  {isRestoring ? (
                    <>
                      <FaSync className="inline mr-2 animate-spin" />
                      {t('deletedProducts.modals.restore.restoring')}
                    </>
                  ) : (
                    t('deletedProducts.modals.restore.restore')
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Permanent Delete Confirmation Modal */}
      <AnimatePresence>
        {showPermanentDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            >
              <div className="flex items-center mb-4">
                <FaExclamationTriangle className="h-6 w-6 text-red-600 mr-3" />
                <h3 className="text-lg font-medium text-gray-900">{t('deletedProducts.modals.permanentDelete.title')}</h3>
              </div>
              <p className="text-gray-600 mb-6">
                {t('deletedProducts.modals.permanentDelete.description')}
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowPermanentDeleteModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t('deletedProducts.modals.permanentDelete.cancel')}
                </button>
                <button
                  onClick={handlePermanentDelete}
                  disabled={isPermanentlyDeleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                >
                  {isPermanentlyDeleting ? (
                    <>
                      <FaSync className="inline mr-2 animate-spin" />
                      {t('deletedProducts.modals.permanentDelete.deleting')}
                    </>
                  ) : (
                    t('deletedProducts.modals.permanentDelete.delete')
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DeletedProductsManager; 